import React, { useMemo } from 'react';
import {
    A4_PAGE_HEIGHT,
    A4_PAGE_WIDTH,
    DEFAULT_PRIMARY_COLOR,
    DEFAULT_SECONDARY_COLOR,
} from '@/constants';
import { cn } from '@/lib/utils';
import { resolvePreviewUrl } from '@/utils/preview-cache';
import { resolveCoverFields, type CoverFieldContext } from './resolve-cover-fields';
import { COVER_TEMPLATES } from './templates/registry';
import { COVER_SANS_FONT, COVER_SERIF_FONT, COVER_TITLE_SCALES } from './templates/shared';
import type { CoverConfig } from '@/types/cover';
import type { ReportData } from '@/types/report';

type CoverPageProps = {
    cover: CoverConfig;
    context: CoverFieldContext;
    /** The laudo's customizations — the brand the cover inherits from. */
    customizations?: ReportData['customizations'];
    className?: string;
};

/**
 * The one shared A4 cover renderer, used by the Capa tab, thumbnails and any
 * future preview. Always renders at the real A4 size (794×1123) with the
 * template's em scale — scale it down with CoverThumbnail, never by width.
 */
export const CoverPage: React.FC<CoverPageProps> = ({
    cover,
    context,
    customizations,
    className,
}) => {
    const template = COVER_TEMPLATES[cover.template_key] ?? COVER_TEMPLATES.classic_center;

    const fields = useMemo(() => resolveCoverFields(cover, context), [cover, context]);

    const colors = {
        primary: cover.colors?.primary || customizations?.colors?.primary || DEFAULT_PRIMARY_COLOR,
        secondary:
            cover.colors?.secondary || customizations?.colors?.secondary || DEFAULT_SECONDARY_COLOR,
    };

    const logoUrl = resolvePreviewUrl(customizations?.header?.logo_url);

    // font unset = the template's native mixed typography (display face on the
    // title via --cover-display, app sans elsewhere); set = the whole cover.
    const displayFont =
        cover.font || (template.titleFont === 'serif' ? COVER_SERIF_FONT : COVER_SANS_FONT);

    return (
        <div
            data-cover-template={template.key}
            className={cn('relative overflow-hidden bg-white text-[#16181d]', className)}
            style={
                {
                    width: A4_PAGE_WIDTH,
                    height: A4_PAGE_HEIGHT,
                    fontSize: A4_PAGE_WIDTH / 30,
                    fontFamily: cover.font || undefined,
                    '--cover-display': displayFont,
                    '--cover-title-scale': COVER_TITLE_SCALES[cover.title_size ?? 'medium'],
                } as React.CSSProperties
            }
        >
            <template.Component
                fields={fields}
                colors={colors}
                logoUrl={logoUrl}
                showLogo={cover.show_logo !== false}
            />
        </div>
    );
};
